import { Tabs as TabsPrimitive } from 'radix-ui'
import type * as React from 'react'
import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useDirectionalIndicator } from '../../hooks/use-directional-indicator'
import { useDocumentDirection } from '../../hooks/use-document-direction'
import { cn } from '../../lib/utils'

type TabsAppearance = 'underline' | 'segmented'

/**
 * Appearance is set once on the root and read by the list and every trigger.
 *
 * The alternative — passing `appearance` to each trigger — is one prop drop
 * away from a tab bar where three tabs are underlined and one is segmented,
 * which nobody notices in review and everybody notices in production.
 */
const TabsAppearanceContext = createContext<TabsAppearance>('underline')
const TabsValueContext = createContext<string | null>(null)

function Tabs({
  className,
  appearance = 'underline',
  dir: providedDir,
  value: controlledValue,
  defaultValue,
  onValueChange,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & { appearance?: TabsAppearance }) {
  // Radix defaults an unspecified direction to LTR and writes it onto the
  // root. Follow the document attribute so a Tabs root does not reset an
  // Arabic subtree back to LTR; an explicit caller direction still wins.
  const documentDirection = useDocumentDirection()
  const dir = providedDir ?? documentDirection
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue)
  const selectedValue = controlledValue === undefined ? uncontrolledValue : controlledValue
  const handleValueChange = useCallback(
    (nextValue: string) => {
      if (controlledValue === undefined) setUncontrolledValue(nextValue)
      onValueChange?.(nextValue)
    },
    [controlledValue, onValueChange],
  )

  return (
    <TabsValueContext.Provider value={selectedValue ?? null}>
      <TabsAppearanceContext.Provider value={appearance}>
        <TabsPrimitive.Root
          data-slot="tabs"
          data-appearance={appearance}
          className={cn('flex flex-col gap-3', className)}
          dir={dir}
          value={controlledValue}
          defaultValue={defaultValue}
          onValueChange={handleValueChange}
          {...props}
        />
      </TabsAppearanceContext.Provider>
    </TabsValueContext.Provider>
  )
}

function TabsList({
  className,
  ref: forwardedRef,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const appearance = useContext(TabsAppearanceContext)
  const selectedValue = useContext(TabsValueContext)
  const listRef = useRef<HTMLDivElement>(null)
  const [overflowing, setOverflowing] = useState(false)
  const setListRefs = useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node
      if (typeof forwardedRef === 'function') {
        const cleanup = forwardedRef(node)
        return cleanup
          ? () => {
              listRef.current = null
              cleanup()
            }
          : undefined
      }
      if (forwardedRef) forwardedRef.current = node
    },
    [forwardedRef],
  )

  useDirectionalIndicator(
    listRef,
    selectedValue,
    '[role="tab"][aria-selected="true"]',
    appearance === 'underline' ? 'underline' : 'segmented',
  )

  useEffect(() => {
    const list = listRef.current
    if (!list) return

    const measure = () => setOverflowing(list.scrollWidth > list.clientWidth)
    measure()
    window.addEventListener('resize', measure)

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    resizeObserver?.observe(list)
    for (const child of list.children) resizeObserver?.observe(child)

    const mutationObserver = new MutationObserver(() => {
      measure()
      for (const child of list.children) resizeObserver?.observe(child)
    })
    mutationObserver.observe(list, { childList: true, subtree: true, characterData: true })

    return () => {
      window.removeEventListener('resize', measure)
      resizeObserver?.disconnect()
      mutationObserver.disconnect()
    }
  }, [])

  return (
    <div
      data-slot="tabs-list-container"
      data-overflowing={overflowing || undefined}
      className="relative min-w-0 after:pointer-events-none after:absolute after:inset-y-0 after:end-0 after:w-8 after:bg-gradient-to-l after:from-card after:to-transparent after:opacity-0 after:transition-opacity data-[overflowing=true]:after:opacity-100 rtl:after:bg-gradient-to-r"
    >
      <TabsPrimitive.List
        ref={setListRefs}
        data-slot="tabs-list"
        data-appearance={appearance}
        data-overflowing={overflowing || undefined}
        className={cn(
          'relative isolate inline-flex max-w-full items-stretch overflow-x-auto overscroll-x-contain',
          appearance === 'underline'
            ? 'w-full gap-2 overflow-y-hidden border-b border-border data-[overflowing=true]:pe-8'
            : 'w-fit flex-wrap gap-0.5 overflow-visible rounded-sm border border-border bg-muted p-0.5',
          className,
        )}
        {...props}
      />
    </div>
  )
}

function TabsTrigger({
  className,
  children,
  count,
  asChild,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> & { count?: number }) {
  const appearance = useContext(TabsAppearanceContext)
  const countElement =
    count === undefined ? null : (
      <span
        data-slot="tabs-count"
        className="rounded-badge bg-muted px-1.5 text-xs text-muted-foreground"
      >
        {count}
      </span>
    )
  const triggerChildren =
    asChild && count !== undefined && isValidElement<{ children?: React.ReactNode }>(children) ? (
      cloneElement(children, undefined, children.props.children, countElement)
    ) : countElement === null ? (
      children
    ) : (
      <>
        {children}
        {countElement}
      </>
    )

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      data-appearance={appearance}
      asChild={asChild}
      className={cn(
        'relative z-10 inline-flex items-center gap-2 text-label whitespace-nowrap transition-ui',
        'disabled:pointer-events-none disabled:text-muted-foreground',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        appearance === 'underline'
          ? [
              // DESIGN.md §5 "Navigation": 38px, the label in secondary ink,
              // the selected tab in ink over a 2px ink rule that sits on the
              // list's hairline (hence -mb-px). The rule is an `::after` so it
              // can draw in from the reading start; lead 5 turns that into the
              // written stroke.
              '-mb-px h-9.5 px-2 text-muted-foreground hover:text-foreground',
              // The list scrolls sideways, which clips anything outside it, so
              // the ring is drawn inside the tab rather than at an offset.
              'focus-visible:outline-3 focus-visible:-outline-offset-3 focus-visible:outline-ring',
              'relative after:absolute after:inset-x-0 after:bottom-px after:h-0.5',
              // `transform-origin` has no logical keyword, so this pair is a
              // deliberate exception to the logical-properties rule.
              'after:origin-left rtl:after:origin-right',
              'after:scale-x-0 after:bg-brand',
              'data-[state=active]:text-foreground data-[state=active]:after:scale-x-100',
              'data-[signature-indicator-hidden=true]:after:!scale-x-0',
            ]
          : [
              // The segmented look, the same as `SegmentedControl`: a muted
              // track, the selected item paper with an `--input` edge.
              'h-7 rounded-badge border border-transparent px-3 text-muted-foreground hover:text-foreground',
              'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
              'data-[state=active]:border-input data-[state=active]:bg-card data-[state=active]:text-foreground',
              'data-[signature-indicator-hidden=true]:!border-transparent data-[signature-indicator-hidden=true]:!bg-transparent',
            ],
        className,
      )}
      {...props}
    >
      {triggerChildren}
    </TabsPrimitive.Trigger>
  )
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  )
}

export { Tabs, TabsContent, TabsList, TabsTrigger }
